import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const FavouritesContext = createContext(null);

export const FavouritesProvider = ({ children }) => {
  const { user, viewAs } = useAuth();
  const [favourites, setFavourites] = useState([]);
  const [favouriteIds, setFavouriteIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  // Fetch favourites when user logs in
  useEffect(() => {
    if (user && !viewAs) {
      fetchFavourites();
    } else {
      setFavourites([]);
      setFavouriteIds(new Set());
    }
  }, [user, viewAs]);

  const fetchFavourites = async () => {
    try {
      setLoading(true);
      const res = await api.get('/auth/favourites');
      const favs = res.data.data || [];
      setFavourites(favs);
      setFavouriteIds(new Set(favs.map(e => e._id)));
    } catch (error) {
      console.error('Error fetching favourites:', error);
    } finally {
      setLoading(false);
    }
  };

  const isFavourite = (eventId) => {
    return favouriteIds.has(eventId);
  };

  const toggleFavourite = async (eventId, eventTitle) => {
    if (!user) {
      toast.error('Please log in to save events');
      return;
    }

    try {
      if (isFavourite(eventId)) {
        // Remove from favourites
        await api.delete(`/auth/favourites/${eventId}`);
        setFavouriteIds(prev => {
          const next = new Set(prev);
          next.delete(eventId);
          return next;
        });
        setFavourites(prev => prev.filter(e => e._id !== eventId));
        toast.success('Removed from favourites');
      } else {
        // Add to favourites
        await api.post(`/auth/favourites/${eventId}`);
        setFavouriteIds(prev => new Set([...prev, eventId]));
        toast.success('Added to favourites');
        // Refresh to get full event data
        fetchFavourites();
      }
    } catch (error) {
      toast.error('Error updating favourites');
    }
  };

  return (
    <FavouritesContext.Provider value={{
      favourites,
      favouriteIds,
      loading,
      isFavourite,
      toggleFavourite,
      fetchFavourites
    }}>
      {children}
    </FavouritesContext.Provider>
  );
};

export const useFavourites = () => useContext(FavouritesContext);
